if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          container: ".m-main-product--wrapper",
          slider: ".m-media-gallery__inner",
          sliderPagination: ".swiper-pagination",
          sliderPrevEl: ".swiper-button-prev",
          sliderNextEl: ".swiper-button-next",
          navSlider: ".nav-swiper-container",
          medias: [".m-product-media--item"],
          mediaWrapper: ".m-product-media--wrapper",
          mediaZoomIns: [".m-product-media__zoom-in"],
          videos: [".media-video"],
          featuredImage: ".m-product-media",
          galleryList: ".m-media-gallery__list",
          galleryInner: ".m-media-gallery__inner",
        };

        this.navSlider = false;
        this.slider = false;
      }

      get context() {
        return this.dataset.context;
      }

      get layout() {
        return this.dataset.layout;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.section = this.closest('product-info');
        this.domNodes = queryDomNodes(this.selectors, this);
        this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === "true";
        this.onlyMedia = this.dataset.onlyMedia === "true";
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.variantPicker = this.section.querySelector("variant-picker");

        this.setSliderOptions();

        const mql = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        
        // Handle window resize to check thumbnail visibility
        let resizeTimeout;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.updateMediaLayout();
          }, 150);
        });
        
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          this.updateMediaLayout();
        });

        if (this.enableImageZoom) {
          this.handlePhotoswipe();
        };

        this.handleAutoplayVideo();

        this.removeAttribute("data-media-loading");
        if (this.domNodes.galleryInner) {
          this.domNodes.galleryInner.style.opacity = 1;
        }

        // Initialize scroll progress bar for list layout
        this.initScrollProgressBar();
      }

      updateMediaLayout() {
        // Check if thumbnail slider container exists and is visible
        const thumbnailContainer = this.querySelector('.m-product-media--slider__thumbnails');
        const isThumbnailVisible = thumbnailContainer && 
          window.getComputedStyle(thumbnailContainer).display !== 'none' &&
          window.getComputedStyle(thumbnailContainer).visibility !== 'hidden';

        // Check if this is list layout
        const wrapper = this.querySelector('.m-media-gallery__wrapper');
        const isListLayout = wrapper?.classList.contains('m-product-media--list');

        if (MinimogTheme.config.mqlMobile) {
          // On mobile, disable Swiper for list layout and use native scroll
          if (isListLayout) {
            // Destroy Swiper first
            if (this.slider) {
              this.destroySlider();
            }
            
            // Clean up Swiper classes and styles
            const galleryInner = this.querySelector('.m-media-gallery__inner');
            const galleryList = this.querySelector('.m-media-gallery__list');
            
            if (galleryInner) {
              // Remove all Swiper classes
              galleryInner.classList.remove(
                'swiper-container',
                'swiper-container-initialized',
                'swiper-container-horizontal',
                'swiper-container-autoheight',
                'swiper-container-ios'
              );
              // Clear inline styles
              galleryInner.style.cssText = galleryInner.style.cssText
                .replace(/transform[^;]*;?/g, '')
                .replace(/transition[^;]*;?/g, '')
                .replace(/height[^;]*;?/g, '');
            }
            
            if (galleryList) {
              // Remove Swiper wrapper class
              galleryList.classList.remove('swiper-wrapper');
              // Clear inline styles
              galleryList.style.cssText = galleryList.style.cssText
                .replace(/transform[^;]*;?/g, '')
                .replace(/transition[^;]*;?/g, '')
                .replace(/height[^;]*;?/g, '');
            }
            
            // Hide duplicate slides after a short delay to ensure DOM is ready
            setTimeout(() => {
              if (galleryList) {
                const duplicateSlides = galleryList.querySelectorAll('.swiper-slide-duplicate');
                duplicateSlides.forEach(slide => {
                  slide.style.display = 'none';
                });
              }
            }, 100);
          } else {
            this.initSlider(isThumbnailVisible);
          }
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider(isThumbnailVisible);
          } else {
            this.destroySlider();
            // Still initialize thumbnail slider if it's visible
            if (isThumbnailVisible) {
              this.initThumbnailSlider();
            }
          }
        }
      }

      setSliderOptions() {
        const { sliderPagination, sliderNextEl: nextEl, sliderPrevEl: prevEl } = this.domNodes;
        
        // Disable loop for list layout on mobile (check will be done in updateMediaLayout)
        const shouldLoop = !this.enableVariantGroupImages;
        
        this.sliderOptions = {
          autoHeight: true,
          navigation: { nextEl, prevEl },
          threshold: 2,
          loop: shouldLoop,
          pagination: {
            el: sliderPagination,
            clickable: true,
            type: "bullets",
          },
          on: {
            init: () => {
              this.domNodes.slider.style.opacity = 1;
              this.domNodes = queryDomNodes(this.selectors, this.section);
              requestAnimationFrame(() => {
                window.dispatchEvent(new Event("resize"));
              });
            }
          }
        };

        this.thumbsOptions = {
          loop: false,
          slidesPerView: 5,
          freeMode: true,
          spaceBetween: 10,
          threshold: 2,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          on: {
            init: (swiper) => {
              if (swiper && swiper.el) {
                swiper.el.style.opacity = '1';
                swiper.el.style.visibility = 'visible';
              }
              // Also update domNodes reference
              if (this.domNodes.navSlider) {
                this.domNodes.navSlider.style.opacity = '1';
                this.domNodes.navSlider.style.visibility = 'visible';
              }
            }
          }
        };

        switch (this.layout) {
          case 'layout-1':
            this.thumbsOptions = {
              ...this.thumbsOptions,
              breakpoints: {
                1024: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                  spaceBetween: 5,
                  freeMode: true,
                  allowTouchMove: false,
                  simulateTouch: false,
                }
              }
            }
            break;
          case 'layout-4':
            break;
          case 'layout-5':
            this.sliderOptions = {
              ...this.sliderOptions,
              watchSlidesProgress: true,
              watchSlidesVisibility: true,
              breakpoints: {
                768: {
                  slidesPerView: 2,
                  slidesPerGroup: 1,
                  spaceBetween: 10,
                },
              }
            }
            break;
          case 'layout-6':
            this.thumbsOptions = {
              ...this.thumbsOptions,
              breakpoints: {
                1024: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                  spaceBetween: 5,
                }
              }
            }
            break;
          case 'layout-7':
            this.sliderOptions = {
              ...this.sliderOptions,
              speed: 500,
              centeredSlides: true,
              breakpoints: {
                768: {
                  slidesPerView: 3
                }
              }
            }
        }
      }

      initSlider(shouldInitThumbnails = true) {
        const { slider, navSlider, galleryList } = this.domNodes;

        // Initialize thumbnail slider if needed
        if (shouldInitThumbnails) {
          this.initThumbnailSlider();
        } else {
          // Destroy thumbnail slider if it exists but shouldn't be shown
          if (typeof this.navSlider === 'object') {
            this.navSlider.destroy(true, true);
            this.navSlider = false;
          }
        }

        // Destroy existing slider if it exists
        if (typeof this.slider === 'object') {
          this.slider.destroy(true, true);
          this.slider = false;
        }

        // Initialize the main slider
        slider.classList.add('swiper-container');
        galleryList.classList.remove('m:grid');
        galleryList.classList.add('swiper-wrapper');

        // Update slider options with thumbnail slider if it exists
        if (this.navSlider) {
          this.sliderOptions.thumbs = {
            swiper: this.navSlider
          };
        } else {
          // Remove thumbs option if no thumbnail slider
          delete this.sliderOptions.thumbs;
        }

        this.slider = new MinimogLibs.Swiper(slider, this.sliderOptions);

        // Handle slide change for variant group images
        if (!this.enableVariantGroupImages) this.handleSlideChange();
      }

      initThumbnailSlider() {
        // Refresh DOM nodes to get latest state
        this.domNodes = queryDomNodes(this.selectors, this);
        const { navSlider } = this.domNodes;

        // Check if thumbnail container exists and is visible
        const thumbnailContainer = this.querySelector('.m-product-media--slider__thumbnails');
        if (!thumbnailContainer || !navSlider) {
          // Destroy if container doesn't exist
          if (typeof this.navSlider === 'object') {
            this.navSlider.destroy(true, true);
            this.navSlider = false;
          }
          return;
        }

        const computedStyle = window.getComputedStyle(thumbnailContainer);
        const isVisible = computedStyle.display !== 'none' &&
          computedStyle.visibility !== 'hidden' &&
          computedStyle.opacity !== '0';

        if (!isVisible) {
          // Destroy if not visible
          if (typeof this.navSlider === 'object') {
            this.navSlider.destroy(true, true);
            this.navSlider = false;
          }
          return;
        }

        // Destroy existing navSlider if it exists
        if (typeof this.navSlider === 'object') {
          this.navSlider.destroy(true, true);
          this.navSlider = false;
        }

        // Ensure navSlider element is ready
        if (!navSlider.querySelector('.swiper-wrapper')) {
          return;
        }

        // Initialize navigation slider
        try {
          this.navSlider = new MinimogLibs.Swiper(navSlider, this.thumbsOptions);

          // Ensure thumbnail slider is visible after initialization
          if (this.navSlider && navSlider) {
            requestAnimationFrame(() => {
              navSlider.style.opacity = '1';
              navSlider.style.visibility = 'visible';
            });
          }
        } catch (error) {
          console.warn('Failed to initialize thumbnail slider:', error);
          this.navSlider = false;
        }
      }

      destroySlider() {
        const { slider, galleryList } = this.domNodes;

        slider.classList.remove('swiper-container');
        galleryList.classList.add('m:grid');
        galleryList.classList.remove('swiper-wrapper');

        if (typeof this.slider === 'object') {
          this.slider.destroy(true, true);
          this.slider = false;
        }

        if (typeof this.navSlider === 'object') {
          this.navSlider.destroy(true, true);
          this.navSlider = false;
        }
      }

      handleSlideChange() {
        if (!this.slider) return;

        this.slider.on("slideChange", (swiper) => {
          window.pauseAllMedia(this);

          const { slides, activeIndex } = swiper;
          const activeSlide = slides[activeIndex];
          if (activeSlide) this.playActiveMedia(activeSlide);

          // Determine visible slides based on layout
          const visibleSlides = [activeIndex];
          if (["layout-5", "layout-7"].includes(this.layout)) {
            visibleSlides.push(activeIndex + 1);
          }

          // Check if any of the visible slides have a model media type
          const hasModelMediaType = visibleSlides.some((index) => {
            const slide = slides[index];
            return slide && slide.dataset.mediaType === "model";
          });

          // Toggle slider's draggable state based on media type
          this.toggleSliderDraggableState(!hasModelMediaType);
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.slider.allowTouchMove !== isDraggable) {
          this.slider.allowTouchMove = isDraggable;
        }
      }

      handleAutoplayVideo() {
        const playVideo = (mediaElement) => {
          const mediaType = mediaElement.dataset.mediaType;
          if (mediaType === "video" || mediaType === "external_video") {
            const deferredMedia = mediaElement.querySelector("deferred-media");
            const autoplay = deferredMedia && deferredMedia.dataset.autoPlay === "true";
            if (autoplay) deferredMedia.loadContent(false);
          }
        };

        if (this.slider) {
          const { slides, activeIndex } = this.slider;
          const activeSlide = slides[activeIndex];
          if (activeSlide) playVideo(activeSlide);
        } else {
          this.domNodes.medias.forEach(playVideo);
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector("deferred-media");
        if (!deferredMedia || deferredMedia.dataset.autoPlay !== "true") return;

        const playMedia = (element) => {
          if (element.classList.contains("js-youtube") || element.classList.contains("js-vimeo")) {
            const platform = element.classList.contains("js-youtube") ? "youtube" : "vimeo";
            const param = platform === "youtube" ? "mute=1" : "muted=1";
            const symbol = element.src.includes("?") ? "&" : "?";
            element.src += `${symbol}autoplay=1&${param}`;
          } else {
            element.play();
          }
        };

        if (!deferredMedia.hasAttribute("loaded")) {
          deferredMedia.loadContent(false);
        }

        const deferredElement = deferredMedia.querySelector("video, model-viewer, iframe");
        playMedia(deferredElement);
      }

      handlePhotoswipe() {
        const medias = [...this.querySelectorAll(".m-product-media--item:not(.swiper-slide-duplicate)")];

        const data = medias.map((media) => {
          const { mediaType, index: id } = media.dataset;

          if (mediaType === "image") {
            const {
              mediaSrc: src,
              mediaWidth,
              mediaHeight,
              mediaAlt: alt,
            } = media.querySelector(".m-product-media").dataset;
            return {
              src,
              width: parseInt(mediaWidth),
              height: parseInt(mediaHeight),
              alt,
              id,
            };
          } else {
            return {
              html: `<div class="pswp__${mediaType}">${media.innerHTML}</div>`,
              type: mediaType,
              id,
            };
          }
        });

        const options = {
          dataSource: data,
          bgOpacity: 1,
          close: false,
          zoom: false,
          arrowNext: false,
          arrowPrev: false,
          counter: false,
          preloader: false,
          pswpModule: MinimogLibs.PhotoSwipeLightbox.PhotoSwipe,
        };
        this.lightbox = new MinimogLibs.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          return this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`) || thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data: { id } }) => {
          const el = this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`);
          return el ? el.src : placeholderSrc;
        });

        this.lightbox.on("change", () => {
          window.pauseAllMedia(this);
          if (this.slider) {
            const { currIndex } = this.lightbox.pswp;
            const slideMethod = this.enableVariantGroupImages ? "slideTo" : "slideToLoop";
            this.slider[slideMethod](currIndex, 100, false);
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp.currSlide.data.type === "model") {
            e.preventDefault();
          }
        });

        // UI elements
        const uiElements = [
          {
            name: "m-close",
            order: 11,
            isButton: true,
            html: '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
            onClick: (event, el, pswp) => pswp.close(),
          },
          {
            name: "m-arrowNext",
            order: 12,
            isButton: true,
            html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
            onClick: (event, el, pswp) => pswp.next(),
          },
          {
            name: "m-arrowPrev",
            order: 10,
            isButton: true,
            html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
            onClick: (event, el, pswp) => pswp.prev(),
          },
        ];

        this.lightbox.on("uiRegister", () => {
          this.lightbox.pswp.ui.registerElement(uiElements[0]);
          if (!this.onlyMedia) {
            uiElements.slice(1).forEach((element) => this.lightbox.pswp.ui.registerElement(element));
          }
        });

        this.lightbox.init();

        const handleMediaClick = (e, media) => {
          e.preventDefault();
          const isImage = media.classList.contains("media-type-image");
          const isZoomButton = e.target.closest(this.selectors.mediaZoomIns[0]);

          if (isImage || isZoomButton) {
            const index = Number(media.dataset.index) || 0;
            this.lightbox.loadAndOpen(index);
          }
        };

        // Handle thumbnail clicks for layout-1 and layout-6
        const handleThumbnailClick = (e, thumbnail) => {
          const thumbnailSlide = thumbnail.closest('.swiper-slide');
          if (thumbnailSlide && thumbnailSlide.dataset.index !== undefined) {
            const index = Number(thumbnailSlide.dataset.index) || 0;
            
            // For layout-1 (list layout), scroll to the corresponding image
            if (this.layout === 'layout-1') {
              const mediaItem = this.querySelector(`.m-product-media--item[data-index="${index}"]`);
              if (mediaItem) {
                // Sadece container içinde scroll yap, sayfa scroll'una neden olma
                const container = mediaItem.closest('.m-media-gallery__inner, .m-media-gallery__list');
                if (container) {
                  const containerRect = container.getBoundingClientRect();
                  const itemRect = mediaItem.getBoundingClientRect();
                  const scrollTop = container.scrollTop + (itemRect.top - containerRect.top) - (containerRect.height / 2) + (itemRect.height / 2);
                  container.scrollTo({
                    top: scrollTop,
                    behavior: 'smooth'
                  });
                } else {
                  mediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                }
              }
            } else if (this.layout === 'layout-6') {
              // For layout-6, don't prevent default to allow Swiper to update the slider
            }
            
            // Open lightbox when thumbnail is clicked (if zoom is enabled)
            if (this.enableImageZoom) {
              setTimeout(() => {
                this.lightbox.loadAndOpen(index);
              }, 100);
            }
          }
        };

        addEventDelegate({
          selector: this.selectors.medias[0],
          context: this,
          handler: handleMediaClick,
        });

        // Add click handler for thumbnails in layout-1 and layout-6
        if (this.layout === 'layout-1' || this.layout === 'layout-6') {
          addEventDelegate({
            selector: '.m-product-media--slider__thumbnails .swiper-slide .m-product-media',
            context: this,
            handler: handleThumbnailClick,
          });
        }
      }

      setActiveMedia(variant) {
        if (!variant) return;
        if (this.slider) {
          this.setActiveMediaForSlider(variant);
        } else {
          this.handleGalleryMode(variant);
        }
      }

      setActiveMediaForSlider(variant) {
        if (variant.featured_media && this.slider && this.slider.wrapperEl) {
          let slideIndex = variant.featured_media.position || 1;
          this.slider.slideToLoop(slideIndex - 1);
        }
      }

      handleGalleryMode(variant) {
        if (this.context !== "featured-product" && variant && variant.featured_media) {
          const selectedMedia = this.querySelector(`.m-media-gallery__list [data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            this.scrollIntoView(selectedMedia);
          }
        }
      }

      scrollIntoView(selectedMedia) {
        // Sadece container içinde scroll yap, sayfa scroll'una neden olma
        const container = selectedMedia.closest('.m-media-gallery__inner, .m-media-gallery__list');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const mediaRect = selectedMedia.getBoundingClientRect();
          
          // Container içinde scroll yap
          const scrollLeft = container.scrollLeft + (mediaRect.left - containerRect.left) - (containerRect.width / 2) + (mediaRect.width / 2);
          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
          });
        } else {
          // Fallback: Eğer container bulunamazsa, scroll yapma
          // selectedMedia.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
      }

      initScrollProgressBar() {
        const dotsContainer = this.querySelector('[data-product-detail-dots]');
        const galleryList = this.querySelector('.m-media-gallery__list');
        const galleryInner = this.querySelector('.m-media-gallery__inner');
        
        if (!dotsContainer || !galleryList || !galleryInner) return;

        // Only show for list layout (layout-1, layout-2, layout-3)
        const wrapper = this.querySelector('.m-media-gallery__wrapper');
        const isListLayout = wrapper?.classList.contains('m-product-media--list');
        
        if (!isListLayout) {
          dotsContainer.style.display = 'none';
          return;
        }

        // Check if mobile
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        if (!isMobile) {
          dotsContainer.style.display = 'none';
          return;
        }

        const mediaItems = galleryList.querySelectorAll('.m-product-media--item');
        const dots = dotsContainer.querySelectorAll('.owl-dot');
        
        // Only hide if single image
        if (mediaItems.length <= 1) {
          dotsContainer.style.display = 'none';
          return;
        }
        
        // Check if scroll is needed
        const checkScrollNeeded = () => {
          const scrollWidth = galleryInner.scrollWidth;
          const clientWidth = galleryInner.clientWidth;
          const maxScroll = scrollWidth - clientWidth;
          return maxScroll > 5; // 5px tolerance
        };
        
        const scrollNeeded = checkScrollNeeded();
        
        if (!scrollNeeded) {
          if (mediaItems.length <= 1) {
            dotsContainer.style.display = 'none';
            return;
          }
        }
        
        // Show dots container
        dotsContainer.style.display = 'block';
        dotsContainer.style.opacity = '1';
        dotsContainer.style.visibility = 'visible';
        dotsContainer.style.width = '100%';
        dotsContainer.style.left = '0';
        dotsContainer.style.right = '0';
        dotsContainer.classList.add('show');
        
        let rafId = null;
        
        const updateActiveDot = () => {
          const scrollLeft = galleryInner.scrollLeft;
          const clientWidth = galleryInner.clientWidth;
          const itemWidth = clientWidth; // Her item tam genişlik
          
          // Hangi görselde olduğunu hesapla
          const currentIndex = Math.round(scrollLeft / itemWidth);
          const activeIndex = Math.min(currentIndex, mediaItems.length - 1);
          
          // Active dot'u güncelle
          dots.forEach((dot, index) => {
            if (index === activeIndex) {
              dot.classList.add('active');
            } else {
              dot.classList.remove('active');
            }
          });
          
          rafId = null;
        };
        
        // Throttled scroll handler
        const scrollHandler = () => {
          if (!rafId) {
            rafId = requestAnimationFrame(updateActiveDot);
          }
        };
        
        // Listen to horizontal scroll on the inner container (native scroll)
        galleryInner.addEventListener('scroll', scrollHandler, { passive: true });
        
        // Initial update after a short delay to ensure layout is ready
        setTimeout(() => {
          updateActiveDot();
        }, 200);
        
        // Update on window resize (throttled)
        let resizeTimeout;
        const resizeHandler = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            updateActiveDot();
          }, 200);
        };
        
        window.addEventListener('resize', resizeHandler, { passive: true });

        // Update on orientation change
        window.addEventListener('orientationchange', () => {
          setTimeout(() => {
            updateProgress();
          }, 300);
        });
      }
    }
  );
}
